/**
 * Dashboards (spec §9.7): named grid layouts of widget instances, synced in the core doc.
 * Which dashboard a device shows is device-local (I-7, `useDevice`).
 */
import { LoroMap, type LoroDoc } from "loro-crdt";
import { ulid } from "ulid";
import { commitMessage } from "../core/actor";
import { C } from "../core/schema";

export interface Widget {
  id: string;
  viewType: string;
  x: number;
  y: number;
  w: number;
  h: number;
  config: Record<string, unknown>;
  title?: string;
  /** i18n key of a preset's title, shown in the device language until the person names the widget */
  titleKey?: string;
}

export interface Dashboard {
  id: string;
  slug: string;
  title: string;
  cols: number;
  rowHeight: number;
  widgets: Record<string, Widget>;
  bundleId?: string;
  bundleVersion?: string;
  /** i18n key when the title comes from a bundle (rendered in the device language) */
  titleKey?: string;
}

export function listDashboards(doc: LoroDoc): Dashboard[] {
  const all = doc.getMap(C.dashboards).toJSON() as Record<string, Dashboard>;
  return Object.values(all)
    .filter((d) => d && typeof d === "object" && d.slug)
    .map((d) => ({ ...d, widgets: d.widgets ?? {} }))
    .sort((a, b) => a.title.localeCompare(b.title));
}

export function findDashboard(doc: LoroDoc, slug: string): Dashboard | undefined {
  return listDashboards(doc).find((d) => d.slug === slug);
}

export function slugify(title: string): string {
  return (
    title
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40) || "dashboard"
  );
}

function uniqueSlug(doc: LoroDoc, base: string, ownId?: string): string {
  const taken = new Set(
    listDashboards(doc)
      .filter((d) => d.id !== ownId)
      .map((d) => d.slug),
  );
  let slug = base;
  for (let n = 2; taken.has(slug); n++) slug = `${base}-${n}`;
  return slug;
}

export function createDashboard(
  doc: LoroDoc,
  input: {
    title: string;
    slug?: string;
    cols?: number;
    rowHeight?: number;
    widgets?: Omit<Widget, "id">[];
    id?: string;
    bundleId?: string;
    titleKey?: string;
  },
): Dashboard {
  const id = input.id ?? ulid();
  const slug = uniqueSlug(doc, input.slug ?? slugify(input.title));
  const m = doc.getMap(C.dashboards).setContainer(id, new LoroMap());
  m.set("id", id);
  m.set("slug", slug);
  m.set("title", input.title);
  if (input.titleKey) m.set("titleKey", input.titleKey);
  m.set("cols", input.cols ?? 12);
  m.set("rowHeight", input.rowHeight ?? 40);
  if (input.bundleId) m.set("bundleId", input.bundleId);
  const widgets = m.setContainer("widgets", new LoroMap());
  for (const w of input.widgets ?? []) {
    const wid = ulid();
    const wm = widgets.setContainer(wid, new LoroMap());
    wm.set("id", wid);
    wm.set("viewType", w.viewType);
    wm.set("x", w.x);
    wm.set("y", w.y);
    wm.set("w", w.w);
    wm.set("h", w.h);
    wm.set("config", w.config);
    if (w.title) wm.set("title", w.title);
    if (w.titleKey) wm.set("titleKey", w.titleKey);
  }
  doc.commit({ message: commitMessage({ op: "createDashboard" }) });
  return findDashboard(doc, slug)!;
}

function dashMap(doc: LoroDoc, id: string): LoroMap {
  const m = doc.getMap(C.dashboards).get(id);
  if (!m || typeof m !== "object" || !("set" in m)) throw new Error("dashboards.noSuchDashboard");
  return m as LoroMap;
}

export function updateDashboard(
  doc: LoroDoc,
  id: string,
  patch: { title?: string; slug?: string; cols?: number; rowHeight?: number },
): void {
  const m = dashMap(doc, id);
  if (patch.title !== undefined) m.set("title", patch.title);
  if (patch.slug !== undefined) m.set("slug", uniqueSlug(doc, slugify(patch.slug), id));
  if (patch.cols !== undefined) m.set("cols", patch.cols);
  if (patch.rowHeight !== undefined) m.set("rowHeight", patch.rowHeight);
  doc.commit({ message: commitMessage({ op: "updateDashboard" }) });
}

export function deleteDashboard(doc: LoroDoc, id: string): void {
  doc.getMap(C.dashboards).delete(id);
  doc.commit({ message: commitMessage({ op: "deleteDashboard" }) });
}

/** Put a deleted dashboard back as it was (same id, slug and widgets): the undo of `deleteDashboard`. */
export function restoreDashboard(doc: LoroDoc, d: Dashboard): void {
  createDashboard(doc, {
    id: d.id,
    title: d.title,
    slug: d.slug,
    titleKey: d.titleKey,
    bundleId: d.bundleId,
    cols: d.cols,
    rowHeight: d.rowHeight,
    widgets: Object.values(d.widgets).map(({ id: _id, ...w }) => w),
  });
}

export function duplicateDashboard(doc: LoroDoc, id: string, title: string): Dashboard {
  const src = listDashboards(doc).find((d) => d.id === id);
  if (!src) throw new Error("dashboards.noSuchDashboard");
  return createDashboard(doc, {
    title,
    cols: src.cols,
    rowHeight: src.rowHeight,
    widgets: Object.values(src.widgets).map(({ id: _id, ...w }) => w),
  });
}

export function addWidget(doc: LoroDoc, dashId: string, w: Omit<Widget, "id">): string {
  const widgets = dashMap(doc, dashId).get("widgets") as LoroMap;
  const wid = ulid();
  const wm = widgets.setContainer(wid, new LoroMap());
  wm.set("id", wid);
  wm.set("viewType", w.viewType);
  wm.set("x", w.x);
  wm.set("y", w.y);
  wm.set("w", w.w);
  wm.set("h", w.h);
  wm.set("config", w.config);
  if (w.title) wm.set("title", w.title);
  doc.commit({ message: commitMessage({ op: "addWidget" }) });
  return wid;
}

/** Where a new widget goes: the shorter of the left and right halves, below what is there. */
export function nextSlot(d: Dashboard, w = 6, h = 8): { x: number; y: number; w: number; h: number } {
  const cols = d.cols || 12;
  const half = Math.floor(cols / 2);
  const bottom = (from: number, to: number) =>
    Math.max(
      0,
      ...Object.values(d.widgets)
        .filter((x) => x.x < to && x.x + x.w > from)
        .map((x) => x.y + x.h),
    );
  const left = bottom(0, half);
  const right = bottom(half, cols);
  return left <= right
    ? { x: 0, y: left, w: Math.min(w, half), h }
    : { x: half, y: right, w: Math.min(w, cols - half), h };
}

export function removeWidget(doc: LoroDoc, dashId: string, widgetId: string): void {
  (dashMap(doc, dashId).get("widgets") as LoroMap).delete(widgetId);
  doc.commit({ message: commitMessage({ op: "removeWidget" }) });
}

export function updateWidget(
  doc: LoroDoc,
  dashId: string,
  widgetId: string,
  patch: Partial<Omit<Widget, "id">>,
): void {
  const wm = (dashMap(doc, dashId).get("widgets") as LoroMap).get(widgetId) as LoroMap | undefined;
  if (!wm) throw new Error("dashboards.noSuchWidget");
  for (const [k, v] of Object.entries(patch)) if (v !== undefined) wm.set(k, v as never);
  doc.commit({ message: commitMessage({ op: "updateWidget" }) });
}

/** One commit for a whole drag/resize pass (one undo step). */
export function setLayout(
  doc: LoroDoc,
  dashId: string,
  layout: { i: string; x: number; y: number; w: number; h: number }[],
): void {
  const widgets = dashMap(doc, dashId).get("widgets") as LoroMap;
  let changed = false;
  for (const l of layout) {
    const wm = widgets.get(l.i) as LoroMap | undefined;
    if (!wm) continue;
    for (const k of ["x", "y", "w", "h"] as const) {
      if (wm.get(k) !== l[k]) {
        wm.set(k, l[k]);
        changed = true;
      }
    }
  }
  if (changed) doc.commit({ message: commitMessage({ op: "setLayout" }) });
}

/** A dashboard's title as shown: a preset's in the device language, until the person renames it. */
export function dashboardTitle(
  t: (key: string, o?: Record<string, unknown>) => string,
  d: Dashboard,
): string {
  return d.titleKey && d.title === t(d.titleKey, { lng: "en" })
    ? t(d.titleKey, { defaultValue: d.title })
    : d.title;
}
